import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTicket, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Whistle } from '@sorare/core/src/atoms/icons/Whistle';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { ComposeRules } from '#src/components/leaderboard/ComposeRules/index.tsx';
import { EligibleCards } from '#src/components/league/EligibleCards/index.tsx';

import { VaultPointsRuleBox } from './VaultPointsRuleBox/index.tsx';
import type {
  TrckGroupAsideHeader_So5Fixture,
  TrckGroupAsideHeader_So5LeaderboardGroupInterface,
  TrckGroupAsideHeader_So5LeagueTrack,
} from './__generated__/index.graphql.ts';

const EntryFeeContainer = styled(SBHorizontal)`
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: 16px;
  border: 1px solid var(--c-nd-50);
  background: #312300;
  backdrop-filter: blur(16px);
  gap: var(--unit);
`;

const Box = styled(Vertical)`
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;

const WhistleContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  aspect-ratio: 1;
  height: 20px;
  width: 20px;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: #312300;
    border: 1px solid var(--c-icon, var(--c-white));
    transform: rotate(45deg);
  }
`;

type Props = {
  withRules?: boolean;
  so5LeagueTrack?: TrckGroupAsideHeader_So5LeagueTrack;
  so5LeaderboardGroupInterface?: TrckGroupAsideHeader_So5LeaderboardGroupInterface;
};

export const TrckGroupAsideHeader = ({
  so5LeagueTrack,
  withRules = true,
  so5LeaderboardGroupInterface,
}: Props) => {
  const [rulesDetailsOpened, setRulesDetailsOpened] = useState(false);
  const { sport } = useSportContext();

  const so5Leaderboard = so5LeagueTrack?.entrySo5Leaderboard;
  const entryItemRule = so5Leaderboard?.displayedTypedRules.find(
    rule => rule.__typename === 'So5EntryItemRule'
  );

  return (
    <Vertical gap={3}>
      <Horizontal>
        <FontAwesomeIcon icon={faUserGroup} size="xs" color="var(--c-nd-600)" />
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            id="FixtureCompetitionLayout.numberPlayers"
            defaultMessage="{count, plural, one {# player} other {# players}}"
            values={{ count: so5LeagueTrack?.so5LineupsCount }}
          />
        </LabelM>
      </Horizontal>
      {!!withRules && (
        <>
          {!!so5LeaderboardGroupInterface &&
            !!so5LeagueTrack?.seasonality &&
            sport === Sport.FOOTBALL && (
              <SBHorizontal>
                <LabelL bold>
                  <FormattedMessage {...fantasy.eligibleCards} />
                </LabelL>
                <Horizontal>
                  <EligibleCards
                    seasonality={so5LeagueTrack?.seasonality}
                    so5LeaderboardGroupInterface={so5LeaderboardGroupInterface}
                  />
                </Horizontal>
              </SBHorizontal>
            )}
          {!!entryItemRule && (
            <EntryFeeContainer>
              <Horizontal>
                <LabelL color="var(--c-yellow-400)" bold>
                  <FontAwesomeIcon icon={faTicket} />
                </LabelL>
                <LabelL color="var(--c-yellow-400)" bold>
                  <FormattedMessage
                    defaultMessage="Unlock access"
                    id="FixtureCompetitionTrckGrpSeasonalityPage.unlockAccess"
                  />
                </LabelL>
              </Horizontal>
              <AnyRewardConfig
                anyRewardConfig={{
                  __typename: 'CardShardRewardConfig',
                  id: 'normalized-card-shard-reward-config',
                  quantity: entryItemRule?.entryItem?.quantity,
                  configRarity: entryItemRule?.entryItem?.rarity,
                  cardSet: null,
                }}
                variant={Variant.INLINE}
              />
            </EntryFeeContainer>
          )}
          <Vertical gap={2}>
            <VaultPointsRuleBox so5Leaderboard={so5Leaderboard} />
            <Box>
              <Accordion
                onExpandChange={setRulesDetailsOpened}
                title={
                  <Horizontal gap={1.5} grow>
                    <WhistleContainer
                      style={
                        {
                          '--c-icon': so5LeagueTrack?.entrySo5Leaderboard
                            .mainRarityType
                            ? getRarityColor(
                                so5LeagueTrack.entrySo5Leaderboard
                                  .mainRarityType
                              )
                            : undefined,
                        } as CSSProperties
                      }
                    >
                      <Whistle color="var(--c-icon)" />
                    </WhistleContainer>
                    <Vertical gap={0.5}>
                      <LabelL bold>
                        <FormattedMessage {...fantasy.rules} />
                      </LabelL>
                      {!!so5LeagueTrack && (
                        <Ellipsis>
                          <LabelM
                            color={
                              rulesDetailsOpened
                                ? 'transparent'
                                : 'var(--c-nd-600)'
                            }
                          >
                            {so5LeagueTrack?.entrySo5Leaderboard.rulesSummary}
                          </LabelM>
                        </Ellipsis>
                      )}
                    </Vertical>
                  </Horizontal>
                }
                noBorder
              >
                {!!so5LeagueTrack && (
                  <ComposeRules
                    so5Leaderboard={so5LeagueTrack.entrySo5Leaderboard}
                  />
                )}
              </Accordion>
            </Box>
          </Vertical>
        </>
      )}
    </Vertical>
  );
};

TrckGroupAsideHeader.fragments = {
  so5Fixture: gql`
    fragment TrckGroupAsideHeader_So5Fixture on So5Fixture {
      slug
    }
  ` as TypedDocumentNode<TrckGroupAsideHeader_So5Fixture>,
  so5LeaderboardGroupInterface: gql`
    fragment TrckGroupAsideHeader_So5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      ...EligibleCards_so5LeaderboardGroupInterface
    }
    ${EligibleCards.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<TrckGroupAsideHeader_So5LeaderboardGroupInterface>,
  so5LeagueTrack: gql`
    fragment TrckGroupAsideHeader_So5LeagueTrack on So5LeagueTrack {
      slug
      entrySo5Leaderboard {
        slug
        rulesSummary
        mainRarityType
        displayedTypedRules {
          id
          key
          ... on So5EntryItemRule {
            id
            entryItem {
              id
              ... on So5CardShardsEntryItem {
                id
                quantity
                rarity
              }
            }
          }
        }
        ...ComposeRules_so5Leaderboard
        ...VaultPointsRuleBox_So5Leaderboard
      }
      seasonality
      so5LineupsCount
    }
    ${ComposeRules.fragments.so5Leaderboard}
    ${VaultPointsRuleBox.fragments.so5Leaderboard}
  ` as TypedDocumentNode<TrckGroupAsideHeader_So5LeagueTrack>,
};
